import React, { useState } from 'react';
import { Zap, MapPin, CheckCircle, AlertCircle, XCircle, ChevronRight, Activity, ExternalLink, RefreshCw } from 'lucide-react';
import useFetchAllStations from '../hooks/useFetchAllStations';
import { useNavigate } from 'react-router-dom';
import useHeartbeat from '../hooks/useHeartbeat';
import { currentGroupId } from "../utils/access";

const GroupStationsSummary = () => {
  const [selectedStation, setSelectedStation] = useState(null);
  const { stations: initialStations, isLoading, error, refetch } = useFetchAllStations({ groupId: currentGroupId() });
  const { stations: heartbeatStations } = useHeartbeat(currentGroupId());
  const navigate = useNavigate();

  const handleRefresh = () => {
    refetch(); // Trigger fresh data fetch
  };

  const handleStationDetailsClick = (stationId) => {
    navigate(`/charging-stations/${stationId}`);
  };

  const handleStationGunClick = (stationId, gunName) => {
    setSelectedStation({ stationId, gunName });
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.ceil((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getStatusText = (statusCode) => {
    switch (statusCode) {
      case 0: return 'Occupied';
      case 1: return 'Available';
      case 2: return 'Available';
      case 3: return 'Faulty';
      default: return 'Available at slow charging';
    }
  };

  // card, icon tile and status-text classes for each status
  const getStatusColor = (statusCode) => {
    switch (statusCode) {
      case 0: return { card: 'border-neutral-200 bg-white hover:border-brand-200', tile: 'bg-brand-50 text-brand-600', text: 'text-brand-700' }; // Occupied
      case 1:
      case 2: return { card: 'border-green-200/80 bg-green-50/40 hover:border-green-300', tile: 'bg-green-100/80 text-green-700', text: 'text-green-700' }; // Available
      case 3: return { card: 'border-red-200/80 bg-red-50/30 hover:border-red-300', tile: 'bg-red-100/70 text-red-600', text: 'text-red-700' }; // Faulty
      default: return { card: 'border-neutral-200 bg-white hover:border-neutral-300', tile: 'bg-neutral-100 text-neutral-500', text: 'text-neutral-600' };
    }
  };

  const getStatusIcon = (statusCode) => {
    switch (statusCode) {
      case 0: return <Activity className="h-4 w-4" />; // Occupied
      case 1:
      case 2: return <CheckCircle className="h-4 w-4" />; // Available
      case 3: return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getDisplayText = (statusCode, timeToSoc) => {
    // Status Code 0 = Occupied
    if (statusCode === 0) {
      const remaining = formatTimeRemaining(timeToSoc);
      return remaining ? `Occupied : Free in ${remaining}` : 'Occupied';
    }

    // Status Codes 1 & 2 = Available
    if (statusCode === 1 || statusCode === 2) {
      return 'Available Now';
    }

    return getStatusText(statusCode);
  };

  // Show loader if first fetch is still loading
  if (isLoading && !initialStations?.length) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-brand-600"></div>
          <p className="text-sm font-medium text-neutral-700">Loading your charging network...</p>
          <p className="mt-1 text-13 text-neutral-500">This will just take a moment</p>
        </div>
      </div>
    );
  }

  // Show error if first fetch fails
  if (error && !initialStations?.length) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="card max-w-md p-8 text-center">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <h2 className="mb-1 text-base font-semibold text-brand-950">Unable to load stations</h2>
          <p className="mb-5 text-13 text-neutral-500">{error}</p>
          <button
            onClick={handleRefresh}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-brand-950">Charging Stations</h1>
          <p className="page-subtitle">Manage and monitor your charging network</p>
        </div>

        <button onClick={handleRefresh} disabled={isLoading} className="btn-secondary self-start sm:self-auto">
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6 2xl:grid-cols-3">
        {initialStations?.map((station) => {
          const hb = heartbeatStations?.find(h => h.stationId.toString() === station.stationId.toString());
          const gunStatus = hb?.gun_status || station.gunStatus || {};
          const timeToSOC = hb?.gun_time_to_soc || station.timeToSOC || {};

          return (
            <article
              key={station.stationId}
              className="flex min-w-0 flex-col rounded-xl border border-neutral-300/80 bg-white p-5 shadow-[0_1px_3px_0_rgb(16_24_40/0.06),0_1px_2px_-1px_rgb(16_24_40/0.04)] transition-[box-shadow,border-color] duration-200 hover:border-neutral-300 hover:shadow-lg"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="min-w-0 truncate text-[15px] font-semibold tracking-tight text-brand-950">
                  {station.stationName || 'Unnamed Station'}
                </h2>
                <button
                  onClick={() => handleStationDetailsClick(station.stationId)}
                  className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-neutral-50 px-2 py-0.5 font-mono text-[11px] font-medium text-neutral-600 ring-1 ring-inset ring-neutral-200 transition-colors hover:bg-brand-50 hover:text-brand-700 hover:ring-brand-200"
                  title="View station details"
                >
                  ID: {station.stationId}
                  <ExternalLink className="h-3 w-3" />
                </button>
              </div>

              <div className="mb-4 mt-2 flex items-start gap-1.5">
                <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
                <div className="min-w-0">
                  <p className="truncate text-13 font-medium text-neutral-700">{station.locationName || 'Unknown Location'}</p>
                  {station.addressLine2 ? <p className="truncate text-xs text-neutral-500">{station.addressLine2}</p> : null}
                </div>
              </div>

              <div className="mt-auto grid grid-cols-1 gap-2.5 border-t border-neutral-100 pt-4 sm:grid-cols-2">
                {Object.keys(gunStatus).length > 0 ? (
                  Object.entries(gunStatus).map(([gunName, statusCode]) => {
                    const tone = getStatusColor(statusCode);
                    return (
                      <div
                        key={gunName}
                        onClick={() => handleStationGunClick(station.stationId, gunName)}
                        className={`group flex min-w-0 cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-colors duration-200 ${tone.card}`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${tone.tile}`}>
                            {getStatusIcon(statusCode)}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-13 font-semibold capitalize text-brand-950">{gunName}</div>
                            <div className={`text-xs font-medium leading-snug ${tone.text}`}>{getDisplayText(statusCode, timeToSOC?.[gunName] || 0)}</div>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-neutral-500" />
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full flex items-center gap-3 rounded-lg border border-dashed border-neutral-200 bg-neutral-50/70 px-3 py-2.5">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white text-neutral-400 ring-1 ring-inset ring-neutral-200">
                      <XCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-13 font-semibold text-neutral-600">No Chargers</div>
                      <div className="text-xs text-neutral-500">No charger data available</div>
                    </div>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default GroupStationsSummary;
